import api from "@/api/url";

export default {
    namespaced: true,

    state: () => {
        return {
            invoices: [],
            cache: new Map(),
            pageData: {
                currentPage: 1,
                totalPages: 0,
                pageSize: 20,
                total: 0,
            },
            selectedProject: {},
            selectedClient: {},
            refreshList: true,
            selectedInvoice: null,
        };
    },

    getters: {
        invoices: state => state.invoices,
        pageData: state => state.pageData,
        selectedProject: state => state.selectedProject,
        selectedClient: state => state.selectedClient,
        refreshList: state => state.refreshList,
        selectedInvoice: state => state.selectedInvoice,
        hasCachedData: (state) => (filters) => {
            const cacheKey = JSON.stringify(filters);
            return state.cache.has(cacheKey);
        },
    },

    mutations: {
        set(state, [key, value]) {
            state[key] = value;
        },
    },
    
    actions: {
        setRefreshList({ commit }, bool) {
            commit("set", ["refreshList", bool]);
        },

        setInvoice({ commit }, payload) {
            commit("set", ["selectedInvoice", payload]);
        },

        sendInvoice({ state, commit, dispatch }, { payload, type = "new", client }) {
            return api.invoice.send(payload)
                .then((resp) => {
                    const data = resp.data.data;
                    const invoices = state.invoices;
                    
                    if (type === "new") {
                        commit("set", ["invoices", [ { ...data, client }, ...invoices ]]);
                        commit("set", ["pageData", {
                            ...state.pageData,
                            total: state.pageData.total + 1,
                            totalPages: Math.ceil((state.pageData.total + 1) / state.pageData.pageSize),
                        }]);
                        return;

                    }

                    dispatch("fetchInvoices")
                        .then(innerResp => {
                            const invoices = innerResp?.data?.data;                        
                            commit("set", ["invoices", invoices]);  
                        })
                        .catch(error => {
                            throw error; 
                        });
                });
        },

        saveInvoiceDraft({ state, commit }, { payload, client, type }) {
            return api.invoice.draft(payload, { type })
                .then((resp) => {
                    const data = resp.data.data;

                    if (type === "new") {
                        const invoices = state.invoices;

                        commit("set", ["invoices", [ 
                            {
                                ...data,
                                client,
                            }, 
                            ...invoices,
                        ]]);
                        commit("set", ["pageData", {
                            ...state.pageData,
                            total: state.pageData.total + 1,
                            totalPages: Math.ceil((state.pageData.total + 1) / state.pageData.pageSize),
                        }]);
                        return;
                    } 

                    return api.invoice.getAll()
                        .then((resp) => {
                            const data = resp?.data?.data;
                            commit("set", ["invoices", data.data]);
                            commit("set", ["pageData", { ...state.pageData, ...data.pageDetails }]);
                        });
                });
        },

        fetchInvoices({ commit, state }, { params = {} }) {
            const cacheKey = JSON.stringify(params);
            const cachedData = state.cache.get(cacheKey);

            // if (cachedData && !refresh) return;

            return api.invoice.getAll(params)
                .then((resp) => {
                    const data = resp?.data?.data;
                    commit("set", ["invoices", data.data]);
                    // commit("set", ["cache", new Map(state.cache).set(cacheKey, { invoices: data.data, timestamp: Date.now() })]);
                    commit("set", ["pageData", { ...state.pageData, ...data.pageDetails }]);
                });
        },

        getInvoice(_, { id, params, config = {} }) {
            return api.invoice.getOne(id, params, config)
                .then((resp) => {                    
                    return  resp?.data?.data;
                });
        },

        updateInvoice({ commit, state }, payload) {      
            return api.invoice.updateOne(payload._id, payload)
                .then((resp) => {
                    const data = resp?.data?.data;   
                    
                    const invoices = state.invoices;
                    const updatedInvoices = invoices.map(c => {
                        if (c?._id === payload?._id) {
                            return {
                                ...c,
                                ...data,
                            };
                        } else {
                            return c;
                        }
                    });
        
                    commit("set", ["invoices", updatedInvoices]);        
                    return data;
                });
        },

        updateManualPayment({ commit, state }, payload) {      
            return api.invoice.updateStatus(payload._id, payload)
                .then((resp) => {
                    const data = resp?.data?.data;   
                    const invoices = state.invoices;
                    const updatedInvoices = invoices.map(c => {
                        if (c?._id === payload?._id) {
                            return {
                                ...c,
                                ...data,
                            };
                        } else {
                            return c;
                        }
                    });
        
                    commit("set", ["invoices", updatedInvoices]);        
                    return data;
                });
        },

        deleteInvoices({ state, commit }, ids) {
            return api.invoice.delete({ ids: ids })
                .then((resp) => {
                    const invoices = state.invoices;
                    const updatedInvoices = invoices.filter(c => {
                        if (Array.isArray(ids)) {
                            return !ids.includes(c?._id);
                        } else {
                            return c._id !== ids;
                        }
                    });
                    const count = state.pageData.total - resp?.data.data?.deletedCount || 0;

                    commit("set", ["invoices", updatedInvoices]);
                    commit("set", ["pageData", {
                        ...state.pageData,
                        total: count,
                        totalPages: Math.ceil(count / 50),
                    }]);
                });
        },

        duplicateInvoice({ commit, state }, { client, payload }) {      
            return api.invoice.duplicate(payload)
                .then((resp) => {
                    const data = resp?.data?.data;
                    
                    commit("set", ["invoices", [{
                        client: client,
                        ...data,
                    }, ...state.invoices ]]);

                    const updatedTotalPages = Math.ceil(state.invoices.length / 50);
                    commit("set", ["pageData", {
                        ...state.pageData,
                        total: state.pageData.total + 1,
                        totalPages: Math.ceil((state.pageData.total + 1) / state.pageData.pageSize),
                    }]);

                    return data;
                });
        },

        downloadInvoice(_, { id, config = {} }) {
            return api.invoice.download(id, config)
                .then((resp) => {
                    return resp;
                });
        },

        setSelectedProject({ commit }, payload) {      
            commit("set", ["selectedProject", payload]);
        },

        setSelectedClient({ commit }, payload) {      
            commit("set", ["selectedClient", payload]);
        },

        updateInvoiceBillingData(_, payload) {                  
            return api.invoice.updateBillingData(payload._id, payload)
                .then((resp) => {                    
                    return resp.data.data;
                });
        },
    },
};